/**
 * Step 4: Import Rules and Message Templates Component
 */

import React, { useRef } from 'react';
import {
  Box,
  Typography,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Card,
  CardContent,
  TextField,
  Button,
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { Step } from '@/api/pipeline';

interface ImportRules {
  dont_import_open_to_work: boolean;
  dont_import_less_that_500_connections: boolean;
  dont_import_no_photo_on_profile: boolean;
  dont_import_free_linkedin_account: boolean;
  dont_import_duplicate_prospects_with_same_owner: boolean;
  delete_other_waiting_prospects_with_same_owner: boolean;
}

interface StepTemplate {
  message_template: string;
  ai_messaging: boolean;
}

interface Step4ImportRulesProps {
  importRules: ImportRules;
  startNow: boolean;
  startTime: Date | null;
  stepTemplates: Record<string, StepTemplate>;
  stepsWithTemplates: Step[];
  onImportRulesChange: (rules: ImportRules) => void;
  onStartNowChange: (startNow: boolean) => void;
  onStartTimeChange: (time: Date | null) => void;
  onStepTemplateChange: (stepUuid: string, template: StepTemplate) => void;
}

const PLACEHOLDERS = [
  { label: 'First Name', value: '%%first_name%%' },
  { label: 'Last Name', value: '%%last_name%%' },
  { label: 'Full Name', value: '%%name%%' },
];

export const Step4ImportRules: React.FC<Step4ImportRulesProps> = ({
  importRules,
  startNow,
  startTime,
  stepTemplates,
  stepsWithTemplates,
  onImportRulesChange,
  onStartNowChange,
  onStartTimeChange,
  onStepTemplateChange,
}) => {
  // Store refs for each text field to track cursor positions
  const textFieldRefs = useRef<Record<string, HTMLInputElement | HTMLTextAreaElement | null>>({});
  // Store cursor positions to preserve them when button is clicked (which causes blur)
  const cursorPositions = useRef<Record<string, { start: number; end: number }>>({});

  const connectionRequestMaxLength = 300;

  const getMessageTemplateError = (step: Step, messageTemplate: string): string | null => {
    // Apply this validation only to connection request steps.
    // Backend Step.Actions.SEND_CONNECTION_REQUEST.value is "Send Connection Request".
    if ((step.action || '') !== 'Send Connection Request') {
      return null;
    }
    if ((messageTemplate || '').length > connectionRequestMaxLength) {
      return `Message is too long for a connection request (${messageTemplate.length}/${connectionRequestMaxLength} characters).`;
    }
    return null;
  };

  const insertPlaceholder = (stepUuid: string, placeholder: string) => {
    const textField = textFieldRefs.current[stepUuid];
    const currentTemplate = stepTemplates[stepUuid]?.message_template || '';
    const currentStepTemplate = stepTemplates[stepUuid] || {
      message_template: '',
      ai_messaging: false,
    };

    let newTemplate = currentTemplate;
    let newCursorPosition = currentTemplate.length + placeholder.length;

    // Try to get cursor position from the text field
    let start = 0;
    let end = 0;

    if (textField) {
      // Get cursor position - use stored position if available (in case field lost focus)
      const storedPos = cursorPositions.current[stepUuid];
      if (storedPos) {
        start = storedPos.start;
        end = storedPos.end;
      } else {
        // Try to get from field directly
        start = textField.selectionStart ?? 0;
        end = textField.selectionEnd ?? 0;
      }

      // Insert placeholder at cursor position
      newTemplate = currentTemplate.substring(0, start) + placeholder + currentTemplate.substring(end);
      newCursorPosition = start + placeholder.length;
    } else {
      // Fallback: append if we can't get cursor position
      newTemplate = currentTemplate + placeholder;
    }

    onStepTemplateChange(stepUuid, {
      ...currentStepTemplate,
      message_template: newTemplate,
    });

    // Update stored cursor position
    cursorPositions.current[stepUuid] = { start: newCursorPosition, end: newCursorPosition };

    // Restore cursor position after state update
    setTimeout(() => {
      const field = textFieldRefs.current[stepUuid];
      if (field && typeof field.setSelectionRange === 'function') {
        field.setSelectionRange(newCursorPosition, newCursorPosition);
        field.focus();
      }
    }, 0);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 2 }}>
      {stepsWithTemplates.length > 0 && (
        <Box>
          <Typography variant="h6" sx={{ mb: 2 }}>Message Templates</Typography>
          {stepsWithTemplates.map((step: Step) => {
            const stepTemplate = stepTemplates[step.key] || {
              message_template: step.message_template || '',
              ai_messaging: step.ai_messaging || false,
            };
            const messageError = getMessageTemplateError(step, stepTemplate.message_template);
            return (
              <Card key={step.key} sx={{ mb: 2 }}>
                <CardContent>
                  <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
                    {step.name}
                  </Typography>
                  <TextField
                    label="Message Template"
                    multiline
                    minRows={4}
                    fullWidth
                    value={stepTemplate.message_template}
                    error={!!messageError}
                    inputRef={(el) => {
                      textFieldRefs.current[step.key] = el;
                    }}
                    onSelect={(e) => {
                      // Store cursor position when user selects text or moves cursor
                      const target = e.target as HTMLInputElement | HTMLTextAreaElement;
                      if (target) {
                        cursorPositions.current[step.key] = {
                          start: target.selectionStart ?? 0,
                          end: target.selectionEnd ?? 0,
                        };
                      }
                    }}
                    onBlur={(e) => {
                      // Store cursor position when field loses focus
                      const target = e.target as HTMLInputElement | HTMLTextAreaElement;
                      if (target) {
                        cursorPositions.current[step.key] = {
                          start: target.selectionStart ?? 0,
                          end: target.selectionEnd ?? 0,
                        };
                      }
                    }}
                    onChange={(e) =>
                      onStepTemplateChange(step.key, {
                        ...stepTemplate,
                        message_template: e.target.value,
                      })
                    }
                    sx={{ mb: 2 }}
                    helperText={messageError || "Use placeholders below to insert prospect data"}
                  />
                  <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                    {PLACEHOLDERS.map((placeholder) => (
                      <Button
                        key={placeholder.value}
                        variant="outlined"
                        size="small"
                        onMouseDown={(e) => {
                          // Prevent button from taking focus from text field
                          e.preventDefault();
                          // Store cursor position before button click
                          const field = textFieldRefs.current[step.key];
                          if (field) {
                            cursorPositions.current[step.key] = {
                              start: field.selectionStart ?? 0,
                              end: field.selectionEnd ?? 0,
                            };
                          }
                        }}
                        onClick={() => insertPlaceholder(step.key, placeholder.value)}
                      >
                        {placeholder.label}
                      </Button>
                    ))}
                  </Box>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={stepTemplate.ai_messaging}
                        onChange={(e) =>
                          onStepTemplateChange(step.key, {
                            ...stepTemplate,
                            ai_messaging: e.target.checked,
                          })
                        }
                      />
                    }
                    label="Use AI Messaging"
                  />
                </CardContent>
              </Card>
            );
          })}
        </Box>
      )}
      
      <Typography variant="h6">Import Rules</Typography>
      <FormGroup>
        <FormControlLabel
          control={
            <Checkbox
              checked={importRules.dont_import_open_to_work}
              onChange={(e) =>
                onImportRulesChange({ ...importRules, dont_import_open_to_work: e.target.checked })
              }
            />
          }
          label="Don't import prospects open to work"
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={importRules.dont_import_less_that_500_connections}
              onChange={(e) =>
                onImportRulesChange({ ...importRules, dont_import_less_that_500_connections: e.target.checked })
              }
            />
          }
          label="Don't import prospects with less than 500 connections"
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={importRules.dont_import_no_photo_on_profile}
              onChange={(e) =>
                onImportRulesChange({ ...importRules, dont_import_no_photo_on_profile: e.target.checked })
              }
            />
          }
          label="Don't import prospects without profile photo"
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={importRules.dont_import_free_linkedin_account}
              onChange={(e) =>
                onImportRulesChange({ ...importRules, dont_import_free_linkedin_account: e.target.checked })
              }
            />
          }
          label="Don't import free LinkedIn accounts"
        />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 0.5 }}>
          Duplicate prospect (same operator): choose one
        </Typography>
        <FormControlLabel
          control={
            <Checkbox
              checked={importRules.dont_import_duplicate_prospects_with_same_owner}
              onChange={(e) =>
                onImportRulesChange({
                  ...importRules,
                  dont_import_duplicate_prospects_with_same_owner: e.target.checked,
                  delete_other_waiting_prospects_with_same_owner: !e.target.checked,
                })
              }
            />
          }
          label="Don't import duplicate prospects with same owner"
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={importRules.delete_other_waiting_prospects_with_same_owner}
              onChange={(e) =>
                onImportRulesChange({
                  ...importRules,
                  delete_other_waiting_prospects_with_same_owner: e.target.checked,
                  dont_import_duplicate_prospects_with_same_owner: !e.target.checked,
                })
              }
            />
          }
          label="Delete other waiting prospects with same owner"
        />
      </FormGroup>
      
      <Box>
        <Typography variant="h6" sx={{ mb: 2 }}>Schedule Job</Typography>
        <FormControlLabel
          control={
            <Checkbox
              checked={startNow}
              onChange={(e) => onStartNowChange(e.target.checked)}
            />
          }
          label="Start job immediately"
        />
        {!startNow && (
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DateTimePicker
              label="Start Time"
              value={startTime}
              onChange={onStartTimeChange}
              slotProps={{
                textField: {
                  fullWidth: true,
                  sx: { mt: 2 },
                },
              }}
            />
          </LocalizationProvider>
        )}
      </Box>
    </Box>
  );
};

